import { Inject, Injectable } from '@angular/core';
import { DbService } from '../db/db.service';
import { AuthService } from '../auth/auth.service';
import { Video } from '../../models/video';
import { Project } from '../../models/project';
import { getFileNameExtension, isNoEmptyArray, isNullOrUndefined, isValidFunction, isValidHttpUrl, isValidString } from '@pineapplelab/util';
import { StorageService } from '../storage/storage.service';
import { Subscription, BehaviorSubject } from 'rxjs';
import firebase from "firebase/compat/app";
import { Course } from '../../models/course';
import { environment } from '../../../environments/environment';
import { FileUtils } from '../../utils/fileUtils';
import { IMediaLibraryHandlerStrategy } from '../../models/util/mediaLibraryHandlerStrategy';
import { MediaLibraryFile } from '../../enums/mediaLibraryFileType.enum';
import { ApiService } from '../api/api.service';
import { BucketVendors } from '../../enums/bucketVendors.enum';
import { ConfigService } from '../config/config.service';
import { ISubtitleMetadata } from '../../models/util/subtitleMetadata';
import { Subtitle } from '../../models/subtitle';
import { TranslationStatusEnum } from '../../enums/translationStatus.enum';

@Injectable({
	providedIn: 'root'
})
export class MediaLibraryService {

	private _videosUploadQueue: { [id: string]: BehaviorSubject<number> } = {};
	private _videosUploadSubscriptions: { [id: string]: Subscription } = {};
	private readonly VALID_MEDIA_LIBRARY_FILES: string[] = ['video/mp4', 'application/pdf', 'image/png', 'image/jpg', 'image/jpeg', 'image/webp'];

	onQueueEmptyCb: () => Promise<void> | void;
	onQueueRefill: () => Promise<void> | void;
	onQueueVideoUploadedCb: (video: Video) => Promise<void> | void;

	constructor(
		protected dbService: DbService,
		protected authService: AuthService,
		protected storageService: StorageService,
		@Inject('MediaLibraryHandlers')
		private mediaLibraryHandlers: IMediaLibraryHandlerStrategy[],
		private api: ApiService,
		private configService: ConfigService,
	) { }

	async getVideos(projectId: string) {
		const snapshot = await this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(Video.PATH).get();
		let videos: Video[] = [];
		snapshot.docs.forEach(item => {
			let data = item.data();
			let video = new Video(data);
			videos.push(video);
		});
		return videos;
	}

	async getVideoById(projectId: string, videoId: string, includeSubtitles: boolean = false) {
		// Subtitles are read alongside the video rather than after it; they only need the id.
		const [snapshot, subtitles] = await Promise.all([
			this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(Video.PATH).doc(videoId).get(),
			includeSubtitles ? this.getSubtitles(videoId) : Promise.resolve(undefined),
		]);
		if (!snapshot.exists) {
			return null;
		}
		let data = snapshot.data();
		let video = new Video(data);
		if (includeSubtitles) {
			video.__subtitles = subtitles;
		}
		return video;
	}

	async save(projectId: string, model: Video[]) {
		this.initializeVideos(model);

		const thumbnails = await this.generateThumbnails(model);
		const fileCopies = await this.prepareFirestoreBatch(projectId, model, thumbnails);

		await this.uploadMediaFiles(model, fileCopies);
	}

	private initializeVideos(model: Video[]) {
		model.forEach(video => {
			if (isNullOrUndefined(video.id)) {
				video.id = this.dbService.generateFirebaseUniqueId();
				video.createdBy = this.authService.auth.user.id;
			}
		});
	}

	private async generateThumbnails(model: Video[]): Promise<(string | null)[]> {
		const thumbnailPromises = model.map(video => {
			const handler = this.getMediaLibraryHandler(video?.fileType);
			return handler ? handler.getThumbnail(video) : null;
		});

		return await Promise.all(thumbnailPromises);
	}

	private async prepareFirestoreBatch(projectId: string, model: Video[], thumbnails: (string | null)[]): Promise<File[]> {
		const videosBatch = this.dbService.firestore.batch();
		const fileCopies: File[] = [];

		model.forEach((media, index) => {
			media.thumbnail = isValidHttpUrl(thumbnails[index]) ? thumbnails[index] : null;

			const videoRef = this.dbService.firestore
				.collection(Project.PATH)
				.doc(projectId)
				.collection(Video.PATH)
				.doc(media.id);

			fileCopies.push(this.createFileCopy(media));
			videosBatch.set(videoRef, media.toJson(), { merge: true });
		});

		await videosBatch.commit();
		return fileCopies;
	}

	private createFileCopy(video: Video): File {
		return new File(
			[video?.__file],
			`media.${getFileNameExtension(video?.__file?.name)}`,
			{ type: video?.__file?.type }
		);
	}

	private async uploadMediaFiles(model: Video[], fileCopies: File[]): Promise<void> {
		const startPromises: Promise<void>[] = [];

		model.forEach((video, index) => {
			this.configureStorageService(video);

			let resolveStarted!: () => void;
			startPromises.push(new Promise<void>((r) => (resolveStarted = r)));

			this._videosUploadSubscriptions[video.id] =
				this.storageService.uploadFileWithListener({ file: fileCopies[index],
					onProgress: (p) => this.processVideoUpload(p, video),
					onSuccess: (url) => this.updateVideoUrl(url, video, fileCopies[index]),
						path: this.getVideoPath(
							video.id,
							'video',
							getFileNameExtension(fileCopies[index].name)
						),
					onError: (e) => this.onVideoProcessError(e, video.id),
					onStarted: resolveStarted,
				});
		});

		await Promise.all(startPromises);
	}

	private configureStorageService(video: Video) {
		const isAws = environment.bucket.vendor === BucketVendors.AWS;
		const shouldUseFirebase = video.fileType !== MediaLibraryFile.VIDEO && isAws;
		this.storageService.setVideoUploaderService(shouldUseFirebase ? BucketVendors.FIREBASE : environment.bucket.vendor);
	}

	async delete(projectId: string, model: Video) {
		await this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(Video.PATH).doc(model.id).delete();
	}

	async saveEdit(projectId: string, model: Video) {
		let json = model.toJson();
		await this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(Video.PATH).doc(model.id).set(json, { merge: true });
	}

	private async updateVideoUrl(videoUrl: string, model: Video, file: File): Promise<void> {
		model.uploaded = true;
		model.video = videoUrl;
		await this.dbService.firestore.collection(Project.PATH).doc(environment.projectId).collection(Video.PATH).doc(model?.id).set({ video: videoUrl, uploaded: true }, { merge: true });
		if (isValidFunction(this.onQueueVideoUploadedCb)) {
			await this.onQueueVideoUploadedCb(model);
		}
		this.removeVideoProcessFromQueue(model?.id);
		if (file && model?.fileType === MediaLibraryFile.VIDEO) {
			this.storageService.transcodeVideo(model?.id);
		}
		if (model?.video && model.fileType === MediaLibraryFile.VIDEO) {
			const languages = this.configService.getConfig().languages.filter(Boolean);
			await this.generateVideoSubtitles(model.video, languages);
		}
	}

	getVideosQueueProgress(): { [id: string]: BehaviorSubject<number> } {
		return this._videosUploadQueue;
	}

	async markVideoUploadAsFailed(id: string): Promise<void> {
		await this.dbService.firestore.collection(Project.PATH).doc(environment.projectId).collection(Video.PATH).doc(id).set({ uploaded: false }, { merge: true });
	}

	private async processVideoUpload(data: string | number, video: Video): Promise<void> {
		if (Object.keys(this._videosUploadQueue).length === 0 && isValidFunction(this.onQueueRefill)) {
			await this.onQueueRefill();
		}

		if (!this._videosUploadQueue[video?.id]) {
			this._videosUploadQueue[video?.id] = new BehaviorSubject<number>(0);
		}

		if (typeof data === 'number') {
			this._videosUploadQueue[video?.id].next(Math.floor(data));
		}
	}

	private async onVideoProcessError(error: any, id: string): Promise<void> {
		await this.markVideoUploadAsFailed(id);
		this.removeVideoProcessFromQueue(id);
	}

	private removeVideoProcessFromQueue(id: string): void {
		if (!this._videosUploadQueue?.[id] || !this._videosUploadSubscriptions?.[id]) {
			return;
		}

		this._videosUploadSubscriptions[id].unsubscribe();

		delete this._videosUploadQueue[id];
		delete this._videosUploadSubscriptions[id];

		if (Object.keys(this._videosUploadQueue).length === 0 && isValidFunction(this.onQueueEmptyCb)) {
			this.onQueueEmptyCb();
		}
	}

	getRef(id: string, projectId: string) {
		if (!id) {
			return null;
		}

		return this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(Video.PATH).doc(id);
	}

	async resolveVideoAssignments(videoId: string, courseId: string, action: 'add' | 'remove', projectId: string): Promise<void> {
		if (!videoId || !courseId) {
			return;
		}

		const courseRef = this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(Course.PATH).doc(courseId);

		const base = this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(Video.PATH).doc(videoId);

		await base.set(
			{
				assigned_to: action === 'remove' ? firebase.firestore.FieldValue.arrayRemove(courseRef) : firebase.firestore.FieldValue.arrayUnion(courseRef),
				updated_at: firebase.firestore.FieldValue.serverTimestamp()
			}, { merge: true }
		);
	}

	private getVideoPath(videoId: string, fileName: string, extension: string): string {
		if (!videoId || !fileName || !extension) {
			return null;
		}

		return `${Project.PATH}/${environment.projectId}/${Video.PATH}/${videoId}/${fileName}.${extension}`;
	}

	areAllFilesValid(files: File[]): boolean {
		return files.every(file => this.isFileValid(file));
	}

	isFileValid(file: File): boolean {
		if (!file) {
			return false;
		}
		return this.VALID_MEDIA_LIBRARY_FILES.some(type => file.type === type);
	}

	async preprocessMediaFiles(files: File[]): Promise<Video[]> {
		if (!files || !isNoEmptyArray(files)) {
			return null;
		}

		const validFiles = files.filter(file => this.isFileValid(file));

		const promises = validFiles.map(file => {
			const handler = this.getMediaLibraryHandlerByFile(file);
			return handler.preprocess(file);
		});

		return Promise.all(promises);
	}

	getMediaLibraryHandler(type: MediaLibraryFile): IMediaLibraryHandlerStrategy {
		return this.mediaLibraryHandlers.find(handler => handler?.type === type);
	}

	private getMediaLibraryHandlerByFile(file: File): IMediaLibraryHandlerStrategy {
		if (!file || !this.isFileValid(file)) {
			return null;
		}

		if (FileUtils.isPDF(file)) {
			return this.getMediaLibraryHandler(MediaLibraryFile.PDF);
		}
		if (FileUtils.isVideo(file)) {
			return this.getMediaLibraryHandler(MediaLibraryFile.VIDEO);
		}
		return this.getMediaLibraryHandler(MediaLibraryFile.IMAGE);
	}

	getValidFilesToString(): string {
		return this.VALID_MEDIA_LIBRARY_FILES.join(',');
	}

	async generateVideoSubtitles(videoUrl: string, languages: string[]): Promise<void> {
		if (!isValidString(videoUrl) || !isNoEmptyArray(languages)) {
			return;
		}

		await this.api.post('/videoSubtitles', {
			publicUrl: videoUrl,
			targetLanguages: languages
		});
	}

	async getSubtitles(videoId: string, languages?: string[]): Promise<ISubtitleMetadata[]> {
		if (!videoId) {
			return [];
		}

		const ref = this.getRef(videoId, environment.projectId);
		const query = await ref.collection(Subtitle.PATH).get();
		if (query.empty) {
			return [];
		}
		const data = query.docs.filter(doc => doc.exists).map(doc => new Subtitle(doc.data()));

		let result: ISubtitleMetadata[] = data
			.filter(item => !item?.isDeleted && item?.translationStatus === TranslationStatusEnum.COMPLETED)
			.map(item => ({
				code: item.language,
				url: item?.subtitleUrl,
				label: `language-translations.${item.language}`
			}));

		if (languages && isNoEmptyArray(languages)) {
			return result.filter(item => languages.some(lang => lang === item?.code));
		}
		return result;
	}
}
